import { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import { useUserById } from 'useCases/users';
import { WithChildren } from 'interfaces/children';
import { useChatContext } from './chat/Chat';
import { useStreamContext } from './Stream';
import { CurrentChatIdContext } from './CurrentChatIdContext';
import { useParams } from 'react-router-dom';

const SOCKET_URL = process.env.REACT_APP_WEB_SOCKET_URL!;
const SURVEY = '/streams';
const CHATS = '/chat';
const REACTIONS = '/reactions';
const NOTIFICATIONS = '/notifications';

const defaultChatSocketParams = {
  path: CHATS,
  forceNew: true,
  transports: ['websocket'],
};

type ContextType = {
  chatSocket: SocketIOClient.Socket;
  surveySocket: SocketIOClient.Socket;
  notificationSocket: SocketIOClient.Socket;
  sessionSocket: SocketIOClient.Socket;
  eventSocket: SocketIOClient.Socket;
  reactionSocket?: SocketIOClient.Socket;
  setReactionSocket: React.Dispatch<React.SetStateAction<SocketIOClient.Socket|undefined>>;
  resetChatSocket: () => void;
};

// type IUser = {
//   name: string;
//   lastName: string;
//   email: string;
// };

const defaultValue: ContextType = {
  chatSocket: io(SOCKET_URL, { autoConnect: false }),
  surveySocket: io(SOCKET_URL, { autoConnect: false }),
  notificationSocket: io(SOCKET_URL, { autoConnect: false }),
  sessionSocket: io(SOCKET_URL, { autoConnect: false }),
  eventSocket: io(SOCKET_URL, { autoConnect: false }),
  reactionSocket: io(SOCKET_URL, { autoConnect: false }),
  setReactionSocket: () => undefined,
  resetChatSocket: () => undefined,
};

export const SocketContext = createContext<ContextType>(defaultValue);

export const SocketProvider = ({ children }: WithChildren) => {
  // const socket = useRef<SocketIOClient.Socket>();
  const [chatSocket, setChatSocket] = useState<SocketIOClient.Socket>(
    defaultValue.chatSocket,
  );

  const [sessionSocket, setSessionSocket] = useState<SocketIOClient.Socket>(
    defaultValue.sessionSocket,
  );
  const [surveySocket, setSurveySocket] = useState<SocketIOClient.Socket>(
    defaultValue.surveySocket,
  );
  const [eventSocket, setEventSocket] = useState<SocketIOClient.Socket>(
    defaultValue.eventSocket,
  );

  const [reactionSocket, setReactionSocket] = useState<SocketIOClient.Socket|undefined>(
    // defaultValue.reactionSocket,
  );
  const [
    notificationSocket,
    setNotificationSocket,
  ] = useState<SocketIOClient.Socket>(defaultValue.notificationSocket);

  const { data } = useUserById(localStorage.getItem('id') || '');
  useEffect(() => {
    if (data?.accessToken) {
      setChatSocket(
        io.connect(SOCKET_URL, {
          ...defaultChatSocketParams,
          auth: {
            accessToken: data?.accessToken,
          },
          secure: SOCKET_URL.includes('https') ? undefined : false,
          query: {
            rand: Math.random().toString(36).substr(2, 9),
          },
        }),
      );
    }
  }, [data]);

  useEffect(() => {
    if (data?.accessToken)
      setNotificationSocket(
        io.connect(SOCKET_URL, {
          path: NOTIFICATIONS,
          auth: {
            accessToken: data?.accessToken,
          },
          transports: ['websocket'],
        }),
      );
  }, [data?.accessToken]);
  useEffect(() => {
    if (data?.accessToken)
      setSessionSocket(
        io.connect(SOCKET_URL, {
          path: '/session',
          auth: {
            accessToken: data?.accessToken,
          },
          secure: SOCKET_URL.includes('https') ? undefined : false,
          transports: ['websocket'],
        }),
      );
  }, [data?.accessToken]);



  useEffect(() => {
    if (data?.accessToken)
      setEventSocket(
        io.connect(SOCKET_URL, {
          path: '/event',
          auth: {
            accessToken: data?.accessToken,
          },
          secure: SOCKET_URL.includes('https') ? undefined : false,
          transports: ['websocket'],
        }),
      );
  }, [data?.accessToken]);

  useEffect(() => {
    if (data?.accessToken)
      setSurveySocket(
        io.connect(SOCKET_URL, {
          path: SURVEY,
          auth: {
            accessToken: data?.accessToken,
          },
          secure: SOCKET_URL.includes('https') ? undefined : false,
          query: {
            rand: Math.random().toString(36).slice(2),
          },
          transports: ['websocket'],
        }),
      );
  }, [data?.accessToken]);

  const resetChatSocket = () => {
    chatSocket.disconnect();
    setChatSocket(
      io.connect(SOCKET_URL, {
        ...defaultChatSocketParams,
        secure: SOCKET_URL.includes('https') ? undefined : false,
        auth: {
          accessToken: data?.accessToken,
        },
      }),
    );
  };

  return (
    <SocketContext.Provider
      value={{
        sessionSocket,
        chatSocket,
        surveySocket,
        notificationSocket,
        eventSocket,
        resetChatSocket,
        reactionSocket,
        setReactionSocket,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocketContext = () => useContext(SocketContext);
